import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import generateAssets from 'common/src/modules/aiContent/store/aiContentProject/thunks/generateAssets';
import {AiContentProjectAgentToolName} from '../aiContentProjectAgent.enums';

const inputSchema = z.object({
  itemIds: z
    .array(z.number())
    .describe('Array of item IDs to generate assets for. Must be explicitly provided.'),
  adSizes: z
    .array(z.string())
    .optional()
    .nullable()
    .describe(
      'Specific ad sizes to generate assets for. If not provided, generates for all ad sizes.',
    ),
  assetKeys: z
    .array(z.string())
    .optional()
    .nullable()
    .describe(
      'Specific asset keys (content feed columns) to generate. If not provided, generates all assets.',
    ),
});

export default function generateAssetsTool(store: CLStore) {
  async function execute({
    input: {itemIds, adSizes, assetKeys},
    toolUtils: {askUserConfirmation},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const state = store.getState();
    const project = state.aiContentProjectPage?.project;
    const projectId = project?.id;

    if (!projectId) {
      return toolResult('Error: No project is currently loaded. Cannot generate assets.');
    }

    if (!itemIds || itemIds.length === 0) {
      return toolResult(
        'Error: No item IDs provided. Please specify the item IDs to generate assets for.',
      );
    }

    // Build confirmation text with filter info
    const filterParts: string[] = [];

    if (adSizes && adSizes.length > 0) {
      filterParts.push(`ad sizes: ${adSizes.join(', ')}`);
    }

    if (assetKeys && assetKeys.length > 0) {
      filterParts.push(`asset keys: ${assetKeys.join(', ')}`);
    }

    const filterInfo = filterParts.length > 0 ? ` (filtered by ${filterParts.join('; ')})` : '';
    const confirmationText = `Generate assets for ${itemIds.length} item${itemIds.length > 1 ? 's' : ''}${filterInfo}?`;

    const shouldProceed = await askUserConfirmation({
      title: 'Generate Assets',
      text: confirmationText,
      okLabel: 'Generate',
      cancelLabel: 'Cancel',
    });

    if (!shouldProceed) {
      return toolResult('Asset generation cancelled by user.');
    }

    try {
      const jobId = await store
        .dispatch(
          generateAssets({
            projectId,
            itemIds,
            adSizes: adSizes ?? undefined,
            assetKeys: assetKeys ?? undefined,
          }),
        )
        .unwrap();

      return toolResult(
        `Asset generation started for ${itemIds.length} item${itemIds.length > 1 ? 's' : ''}${filterInfo} (Job ID: ${jobId}). The items will show "Generating Assets" status until complete.`,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);

      return toolResult(`Error generating assets: ${errorMessage}`);
    }
  }

  return tool({
    displayName: 'Generating assets',
    name: AiContentProjectAgentToolName.GenerateAssets,
    description:
      'Generates AI assets (images, text content) for the specified items based on their prompts. Items must have prompts generated first. Item IDs must be explicitly provided. Optionally filter by ad sizes or asset keys.',
    execute,
    inputSchema,
  });
}
